import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../auth/AuthContext';
import type { BulletinListItem, BulletinReadState } from '../constants/bulletin';
import { resolveStaffAvatarSrc } from '../constants/defaultAvatar';
import { bulletinMentionedRowSurfaceSx } from '../constants/bulletinMentionUi';
import { formatDisplayExpiresAt, formatPostTime } from '../utils/formatPostTime';
import BulletinPreviewBody from './BulletinPreviewBody';

type Props = {
  post: BulletinListItem;
  onOpen: () => void;
  /** アバター一辺の px（一覧はやや大きめ、ダッシュボードは狭いカラム向けに小さく） */
  avatarSize?: number;
};

function readStateLabel(state: BulletinReadState): string | null {
  switch (state) {
    case 'unread':
      return '未読';
    case 'post_edited':
      return '編集あり';
    case 'new_comments':
      return '新規コメント';
    default:
      return null;
  }
}

/**
 * 掲示板一覧・ダッシュボード用
 */
const BulletinFeedPostRow: React.FC<Props> = ({ post, onOpen, avatarSize = 48 }) => {
  const { staffProfile } = useAuth();
  const myStaffId = staffProfile?.id;
  const hasPreview = Boolean(post.body_preview && post.body_preview.trim());
  const stateLabel = readStateLabel(post.bulletin_read_state);
  const needsAttention = post.bulletin_read_state !== 'read';
  const mentioned = Boolean(post.is_mentioned_to_me);
  /** 既読以外（未読・編集あり・新規コメント）。未読のみバッジ・フィルタと同じ判定 */
  const isUnreadRow = post.is_unread;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        cursor: 'pointer',
        textAlign: 'left',
        outline: 'none',
        ...(mentioned
          ? {
              ...bulletinMentionedRowSurfaceSx(false),
              '&:hover': bulletinMentionedRowSurfaceSx(true),
            }
          : isUnreadRow
            ? {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                '&:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12) },
              }
            : {
                '&:hover': { bgcolor: 'action.hover' },
              }),
        '&:focus-visible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Avatar
        src={resolveStaffAvatarSrc(post.author_avatar_url)}
        alt=""
        sx={{ width: avatarSize, height: avatarSize, fontSize: avatarSize * 0.38, flexShrink: 0, mt: 0.25 }}
      >
        {(post.author_display_name || '?').charAt(0)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 0.5, rowGap: 0 }}>
          <Typography component="span" variant="subtitle2" fontWeight={700} sx={{ color: 'text.primary' }}>
            {post.author_display_name}
          </Typography>
          <Typography component="span" variant="caption" color="text.secondary">
            · {formatPostTime(post.updated_at)}
          </Typography>
          {stateLabel && (
            <Typography
              component="span"
              sx={{
                ml: 0.5,
                px: 0.75,
                py: 0.125,
                borderRadius: 1,
                fontSize: '0.65rem',
                fontWeight: 400,
                lineHeight: 1.3,
                border: '1px solid',
                borderColor:
                  post.bulletin_read_state === 'unread'
                    ? 'primary.main'
                    : post.bulletin_read_state === 'post_edited'
                      ? 'warning.main'
                      : 'info.main',
                color:
                  post.bulletin_read_state === 'unread'
                    ? 'primary.main'
                    : post.bulletin_read_state === 'post_edited'
                      ? 'warning.main'
                      : 'info.main',
                bgcolor: 'transparent',
              }}
            >
              {stateLabel}
            </Typography>
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{
            mt: 0.25,
            fontWeight: 700,
            color: needsAttention ? 'text.primary' : 'text.secondary',
          }}
        >
          {post.title}
        </Typography>
        {post.display_expires_at && new Date(post.display_expires_at) > new Date() && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            表示期限 {formatDisplayExpiresAt(post.display_expires_at)} まで
          </Typography>
        )}
        {hasPreview && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.75,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <BulletinPreviewBody body={post.body_preview} myStaffId={myStaffId} />
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BulletinFeedPostRow;
